import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Calendar } from 'primereact/calendar'
import { Dropdown, DropdownProps } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../../components/Panel'
import { InputNumberToggleable } from '../../../../../components/inputs'
import { usePanelContext } from '../../../../../contexts/panel'
import { useSelectFirstAndOnlyOption } from '../../../../../hooks'
import { getConfigTranslation } from '../../../../../utils/helper/getConfigTranslation'
import { ConfigurableComponent } from '../../../../../utils/wrapper'
import { DefaultConfig, FieldOption } from '../../../../ui-config/data'
import { DataForArticleDto } from '../../../data/model'
import { FormFieldName } from './ArticleDetailForm.form'

type Props = {
  config: DefaultConfig
  formik: FormikProps<DataForArticleDto>
  isFormFieldValid: (name: FormFieldName) => boolean
  getFormErrorMessage: (name: FormFieldName) => false | JSX.Element
  isLoading: boolean
  hasBatches: boolean
  openQuantity: number
  children?: JSX.Element
}

export const ArticleDetailFormView: React.FC<Props> = ({
  config,
  formik,
  isFormFieldValid,
  getFormErrorMessage,
  isLoading,
  hasBatches,
  openQuantity,
  children,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n

  const panelId = 'delivery-forecast-despatch-advice-article-detail'
  const { isEdit } = usePanelContext()

  // ************************************************************
  // components
  // ************************************************************
  // desAdvPosition
  const desAdvPositionLabel = (
    <label className="text-sm" htmlFor="desAdvPosition"></label>
  )

  const desAdvPositionComponent = (
    <InputText
      id="desAdvPosition"
      value={formik.values.desAdvPosition}
      className="w-full"
      disabled
    />
  )

  const desAdvPositionWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={desAdvPositionComponent}
      label={desAdvPositionLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // buyerProductId
  const buyerProductIdLabel = (
    <label className="text-sm" htmlFor="buyerProductId"></label>
  )

  const buyerProductIdComponent = (
    <InputText
      id="buyerProductId"
      value={formik.values.buyerProductId}
      className="w-full"
      disabled
    />
  )

  const buyerProductIdWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={buyerProductIdComponent}
      label={buyerProductIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // buyerProductDescription
  const buyerProductDescriptionLabel = (
    <label className="text-sm" htmlFor="buyerProductDescription"></label>
  )

  const buyerProductDescriptionComponent = (
    <InputText
      id="buyerProductDescription"
      value={formik.values.buyerProductDescription}
      className="w-full"
      disabled
    />
  )

  const buyerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={buyerProductDescriptionComponent}
      label={buyerProductDescriptionLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationItemId
  const confirmationItemIdLabel = (
    <label className="text-sm" htmlFor="confirmationItemId"></label>
  )

  const confirmationItemIdComponent = (
    <InputText
      id="confirmationItemId"
      value={formik.values.confirmationItemId}
      className="w-full"
      disabled
    />
  )

  const confirmationItemIdWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={confirmationItemIdComponent}
      label={confirmationItemIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // quantity
  const quantityLabel = <label htmlFor="quantity" className="text-sm"></label>

  const quantityComponent = (
    <InputNumber
      id="quantity"
      value={formik.values.quantity}
      className="w-full"
      locale={language}
      maxFractionDigits={3}
      disabled
    />
  )

  const quantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={quantityComponent}
      label={quantityLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // openQuantity
  const openQuantityLabel = (
    <label htmlFor="openQuantity" className="text-sm"></label>
  )

  const openQuantityComponent = (
    <InputNumber
      id="openQuantity"
      value={formik.values.openQuantity}
      className="w-full"
      locale={language}
      maxFractionDigits={3}
      disabled
    />
  )

  const openQuantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={openQuantityComponent}
      label={openQuantityLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationQuantity
  const confirmationQuantityLabel = (
    <label
      htmlFor="confirmationQuantity"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationQuantity'),
      })}
    ></label>
  )

  const handleConfirmationQuantityChange = async (value: number | null) => {
    await formik.setFieldValue('confirmationQuantity', value)
    await formik.setFieldValue('deliveryStatus', 'NOT_SET')
  }

  const confirmationQuantityComponent = (
    <InputNumber
      id="confirmationQuantity"
      value={formik.values.confirmationQuantity}
      onChange={e => {
        handleConfirmationQuantityChange(e.value)
      }}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationQuantity'),
      })}
      locale={language}
      disabled={hasBatches || !isEdit(panelId)}
    />
  )

  const confirmationQuantityErrorMsg = (
    <div>{getFormErrorMessage('confirmationQuantity')}</div>
  )

  const confirmationQuantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={confirmationQuantityComponent}
      label={confirmationQuantityLabel}
      className="mb-1"
      errorMsg={confirmationQuantityErrorMsg}
      isLoading={isLoading}
    />
  )

  // deliveryStatus
  const deliveryStatusLabel = (
    <label
      htmlFor="deliveryStatus"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('deliveryStatus'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'deliveryStatus',
    config.deliveryForecastDespatchAdvice.articleDetail.deliveryStatus.options,
    formik,
    isEdit(panelId),
  )

  const deliveryStatusComponent = (
    <Dropdown
      id="deliveryStatus"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('deliveryStatus'),
      })}
      value={formik.values.deliveryStatus}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
      disabled={!isEdit(panelId)}
    />
  )

  const deliveryStatusErrorMsg = (
    <div>{getFormErrorMessage('deliveryStatus')}</div>
  )

  const deliveryStatusWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={deliveryStatusComponent}
      label={deliveryStatusLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={deliveryStatusErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationNetWeight
  const confirmationNetWeightLabel = (
    <label
      htmlFor="confirmationNetWeight"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationNetWeight'),
      })}
    ></label>
  )

  const confirmationNetWeightComponent = (
    <InputNumberToggleable
      id="confirmationNetWeight"
      checked={formik.values.confirmationNetWeightChecked}
      onCheckedChange={e =>
        formik.setFieldValue('confirmationNetWeightChecked', e.checked)
      }
      value={formik.values.confirmationNetWeight}
      mode="decimal"
      locale={language}
      onChange={e => formik.setFieldValue('confirmationNetWeight', e.value)}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationNetWeight'),
      })}
      min={0}
      minFractionDigits={3}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationNetWeightErrorMsg = (
    <div>{getFormErrorMessage('confirmationNetWeight')}</div>
  )

  const confirmationNetWeightWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={confirmationNetWeightComponent}
      label={confirmationNetWeightLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationNetWeightErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationNetWeightUnit
  const confirmationNetWeightUnitLabel = (
    <label
      htmlFor="confirmationNetWeightUnit"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationNetWeightUnit'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'confirmationNetWeightUnit',
    config.deliveryForecastDespatchAdvice.articleDetail
      .confirmationNetWeightUnit.options,
    formik,
    isEdit(panelId),
  )

  const confirmationNetWeightUnitComponent = (
    <Dropdown
      id="confirmationNetWeightUnit"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationNetWeightUnit'),
      })}
      value={formik.values.confirmationNetWeightUnit}
      onChange={e => formik.setFieldValue('confirmationNetWeightUnit', e.value)}
      optionLabel="code"
      optionValue="code"
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationNetWeightUnitErrorMsg = (
    <div>{getFormErrorMessage('confirmationNetWeightUnit')}</div>
  )

  const confirmationNetWeightUnitWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={confirmationNetWeightUnitComponent}
      label={confirmationNetWeightUnitLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationNetWeightUnitErrorMsg}
      isLoading={isLoading}
    />
  )

  // lastReceivingDate
  const lastReceivingDateLabel = (
    <label className="text-sm" htmlFor="lastReceivingDate"></label>
  )

  const lastReceivingDate = formik.values.lastReceiving?.Date
    ? new Date(formik.values.lastReceiving.Date)
    : null

  const lastReceivingDateComponent = (
    <Calendar
      id="lastReceivingDate"
      value={lastReceivingDate}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const lastReceivingDateWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={lastReceivingDateComponent}
      label={lastReceivingDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // lastReceivingQuantity
  const lastReceivingQuantityLabel = (
    <label className="text-sm" htmlFor="lastReceivingQuantity"></label>
  )

  const lastReceivingQuantityComponent = (
    <InputNumber
      id="lastReceivingQuantity"
      className="w-full"
      value={formik.values.lastReceiving?.Quantity}
      locale={language}
      disabled
    />
  )

  const lastReceivingQuantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={lastReceivingQuantityComponent}
      label={lastReceivingQuantityLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationCountryOfOrigin
  const confirmationCountryOfOriginLabel = (
    <label
      htmlFor="confirmationCountryOfOrigin"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationCountryOfOrigin'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'confirmationCountryOfOrigin',
    config.deliveryForecastDespatchAdvice.articleDetail
      .confirmationCountryOfOrigin.options,
    formik,
    isEdit(panelId),
  )

  function getFlagEmoji(countryCode: string) {
    return countryCode
      .toUpperCase()
      .replace(/./g, char => String.fromCodePoint(127397 + char.charCodeAt(0)))
  }

  const countryTemplate = (countryCode: string, countryName: string) => (
    <>
      <div className="relative flex align-items-center">
        <span className="absolute text-3xl">{getFlagEmoji(countryCode)}</span>
        <span className="ml-5">{countryName}</span>
      </div>
    </>
  )

  const selectedCountryTemplate = (
    option: FieldOption,
    props: DropdownProps,
  ) => {
    if (option) {
      return countryTemplate(option.code, getConfigTranslation(option.name))
    }

    return <span>{props.placeholder}</span>
  }

  const countryOptionTemplate = (option: FieldOption) => {
    return countryTemplate(option.code, getConfigTranslation(option.name))
  }

  const confirmationCountryOfOriginComponent = (
    <Dropdown
      id="confirmationCountryOfOrigin"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationCountryOfOrigin'),
      })}
      value={formik.values.confirmationCountryOfOrigin}
      onChange={e =>
        formik.setFieldValue('confirmationCountryOfOrigin', e.value)
      }
      filter
      optionLabel={`name.${language}`}
      optionValue="code"
      valueTemplate={selectedCountryTemplate}
      itemTemplate={countryOptionTemplate}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationCountryOfOriginErrorMsg = (
    <div>{getFormErrorMessage('confirmationCountryOfOrigin')}</div>
  )

  const confirmationCountryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.articleDetail}
      component={confirmationCountryOfOriginComponent}
      label={confirmationCountryOfOriginLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationCountryOfOriginErrorMsg}
      isLoading={isLoading}
    />
  )

  return (
    <PanelContainer
      formik={formik}
      header={getConfigTranslation(
        config.deliveryForecastDespatchAdvice.articleDetail.title,
      )}
      id={panelId}
      visible={
        config.deliveryForecastDespatchAdvice.articleDetail.enabled !== false
      }
    >
      <>
        <form onSubmit={formik.handleSubmit}>
          <div className="formgrid grid">
            {/* desAdvPosition */}
            {desAdvPositionWrapper}

            {/* buyerProductId */}
            {buyerProductIdWrapper}

            {/* buyerProductDescription */}
            {buyerProductDescriptionWrapper}

            {/* confirmationItemId */}
            {confirmationItemIdWrapper}

            {/* confirmationQuantity, deliveryStatus  */}
            <div className="col-12">
              <div className="formgrid grid">
                {quantityWrapper}

                {openQuantityWrapper}

                <div className="col-12 md:col-6 lg:col-4 xl:col-3">
                  {confirmationQuantityWrapper}

                  {/* Only show this help text if there is an over  or under delivery */}
                  {openQuantity !== 0 &&
                    !formik.errors.confirmationQuantity && (
                      <small id="open-quantity" className="block mb-3">
                        {openQuantity > 0
                          ? t(
                              'deliveryForecastDespatchAdvice.articleDetail.form.confirmationQuantity.validation.underDelivery',
                              {
                                quantity:
                                  Math.abs(openQuantity).toLocaleString(
                                    language,
                                  ),
                              },
                            )
                          : t(
                              'deliveryForecastDespatchAdvice.articleDetail.form.confirmationQuantity.validation.overDelivery',
                              {
                                quantity:
                                  Math.abs(openQuantity).toLocaleString(
                                    language,
                                  ),
                              },
                            )}
                      </small>
                    )}
                </div>

                {/* Only show this field if there is an under delivery */}
                {openQuantity > 0 && deliveryStatusWrapper}
              </div>
            </div>

            <div className="col-12">
              <div className="formgrid grid">
                {/* confirmationNetWeight */}
                {confirmationNetWeightWrapper}

                {/* confirmationNetWeightUnit */}
                {confirmationNetWeightUnitWrapper}
              </div>
            </div>

            <div className="col-12">
              <div className="formgrid grid">
                {/* confirmationNetWeight */}
                {lastReceivingDateWrapper}

                {/* confirmationNetWeightUnit */}
                {lastReceivingQuantityWrapper}
              </div>
            </div>

            {/* confirmationCountryOfOrigin */}
            {confirmationCountryOfOriginWrapper}
          </div>
        </form>
        {children}
      </>
    </PanelContainer>
  )
}
